import { useState } from 'react';

import {
  Alert,
  Anchor,
  Box,
  Button,
  ButtonGroup,
  FieldSet,
  FormField,
  Heading,
  Icon,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  Text,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { IconMailFilled, IconTrophyFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { invalidateListingsPageQueries } from '../../../hooks/queries/listings';
import emailUtils from '../../../utils/emailUtils';

import { useAuth } from '../../AuthProvider';

import type { ValueOf } from 'type-fest';
import type { RentalManagerDeactivateListingReason } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: string;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
};

const STEPS = {
  REASON: 'reason',
  CONFIRMATION_THANKS: 'confirmationThanks',
  CONFIRMATION_HAS_ERROR: 'confirmationHasError',
} as const;

type Step = ValueOf<typeof STEPS>;

const RADIO_OPTIONS = [
  {
    key: 'FOUND_RENTER_ON_ZILLOW',
    displayLabel: 'Found a new renter through Zillow',
    reason: 'FOUND_RENTER_ON_ZILLOW' as RentalManagerDeactivateListingReason,
    step: STEPS.CONFIRMATION_THANKS,
  },
  {
    key: 'FOUND_RENTER_ELSEWHERE',
    displayLabel: 'Found a new renter elsewhere',
    reason: 'FOUND_RENTER_ELSEWHERE' as RentalManagerDeactivateListingReason,
    step: STEPS.CONFIRMATION_THANKS,
  },
  {
    key: 'STILL_LOOKING_PAUSING_ON_ZILLOW',
    displayLabel: 'Still looking for a renter, but pausing this Zillow listing',
    reason: 'STILL_LOOKING_PAUSING_ON_ZILLOW' as RentalManagerDeactivateListingReason,
    step: STEPS.CONFIRMATION_THANKS,
  },
  {
    key: 'HAS_ERROR',
    displayLabel: 'There\u2019s an error with the listing that I need to resolve',
    reason: 'ERROR_WITH_LISTING' as RentalManagerDeactivateListingReason,
    step: STEPS.CONFIRMATION_HAS_ERROR,
  },
] as const;

const FeedListingDeactivateModal = ({ listingAlias, open = false, onOpenChange }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const [step, setStep] = useState<Step>(STEPS.REASON);
  const [selectedReasonKey, setSelectedReasonKey] = useState<string | null>(null);
  const [listingIssueReason, setListingIssueReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const { mutate: deactivateListing, isPending } =
    useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing();

  const resetState = () => {
    setError(null);
    setSelectedReasonKey(null);
    setListingIssueReason('');
    setStep(STEPS.REASON);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onOpenChange(false);
      resetState();
    } else {
      onOpenChange(true);
    }
  };

  const handleDeactivate = () => {
    const selected = RADIO_OPTIONS.find((opt) => opt.key === selectedReasonKey);
    if (!selected) return;

    const comment = selected.key === 'HAS_ERROR' && listingIssueReason ? listingIssueReason : undefined;

    deactivateListing(
      {
        postData: {
          listingAlias,
          reason: selected.reason,
          ...(comment ? { commentStr: comment } : {}),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
          });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
          });
          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
          invalidateListingsPageQueries(queryClient);
          setError(null);
          setStep(selected.step);
        },
        onError: () => {
          setError('There was an error. Please try again');
        },
      },
    );
  };

  const isMultifamily = overviewData?.isMultifamily ?? false;
  const isMultifamilyCustomer = user?.isMultifamilyCustomer ?? false;
  const isSingleFamilyEnterpriseCustomer = user?.isSingleFamilyEnterpriseCustomer ?? false;
  const helpEmail = emailUtils.getHelpEmail(isMultifamily && isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer);

  const heading =
    step === STEPS.REASON
      ? 'Deactivate this listing?'
      : step === STEPS.CONFIRMATION_HAS_ERROR
        ? 'How to fix listing errors'
        : 'Thanks for choosing Zillow!';

  return (
    <Modal
      data-testid="FeedListingDeactivateModal"
      open={open}
      onOpenChange={handleOpenChange}
      header={
        <Heading level={2} textStyle="responsive.heading-xs" css={{ textAlign: 'left' }}>
          {heading}
        </Heading>
      }
      body={
        <>
          {error && (
            <Box css={{ marginBottom: 'tightest' }}>
              <Alert tone="warning">{error}</Alert>
            </Box>
          )}

          {step === STEPS.REASON && (
            <>
              <FieldSet
                legend={
                  <Legend>
                    The listing will no longer be advertised. Please choose your reason for deactivating it.
                  </Legend>
                }
              >
                {RADIO_OPTIONS.map((opt) => (
                  <LabeledControl
                    key={opt.key}
                    controlId={opt.key}
                    labelPosition="right"
                    label={<Label>{opt.displayLabel}</Label>}
                    control={
                      <Radio
                        name="deactivate-reason"
                        checked={selectedReasonKey === opt.key}
                        onChange={() => setSelectedReasonKey(opt.key)}
                      />
                    }
                  />
                ))}
              </FieldSet>
              {selectedReasonKey === 'HAS_ERROR' && (
                <FormField
                  css={{ marginTop: '400' }}
                  label={<Label>Describe the listing issue</Label>}
                  control={
                    <Textarea
                      maxLength={850}
                      value={listingIssueReason}
                      onChange={(e) => setListingIssueReason(e.target.value)}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                    />
                  }
                  description={
                    <CharacterCountFormHelp
                      currentLength={listingIssueReason.length}
                      maxLength={850}
                      isFocused={isFocused}
                      label="listing issue"
                    />
                  }
                />
              )}
            </>
          )}

          {step === STEPS.CONFIRMATION_THANKS && (
            <>
              <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconTrophyFilled />} />
              <Paragraph css={{ textStyle: 'heading-sm', marginBlock: '400' }}>
                This listing will be deactivated as of today.
              </Paragraph>
              <Paragraph>Your advertising period will be included on your next invoice.</Paragraph>
            </>
          )}

          {step === STEPS.CONFIRMATION_HAS_ERROR && (
            <>
              <Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconMailFilled />} />
              <Text textStyle="heading-sm" asChild css={{ marginBlock: '400' }}>
                <p>Contact a Zillow representative</p>
              </Text>
              <Text asChild>
                <p>
                  Please email&nbsp;
                  <Anchor href={`mailto:${helpEmail}`}>{helpEmail}</Anchor>
                  &nbsp;for help with questions about your listing.
                </p>
              </Text>
            </>
          )}
        </>
      }
      footer={
        step === STEPS.REASON ? (
          <ButtonGroup aria-label="deactivation actions">
            <TextButton
              css={{ paddingLeft: '400', paddingRight: '400' }}
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </TextButton>
            <Button
              emphasis="filled"
              tone="brand"
              onClick={handleDeactivate}
              disabled={selectedReasonKey === null || isPending}
              loading={isPending}
            >
              Yes, deactivate listing
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup aria-label="close confirmation">
            <Button emphasis="filled" tone="brand" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </ButtonGroup>
        )
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default FeedListingDeactivateModal;
